import * as React from 'react';
import { MatrixBody } from '../types';

const { useState } = React;

type DistanceDisplayUnit = 'km' | 'm' | 'mi';
type TimeDisplayUnit = 'sec' | 'min' | 'hr';

interface Props {
  response: any;
  body: MatrixBody;
  elapsedMs: number;
}

// --- Distance conversion helpers ---
/** Convert a distance value from the API unit to the desired display unit. */
function convertDistance(value: number, fromUnit: string, toUnit: DistanceDisplayUnit): number {
  // First normalize to meters
  let meters: number;
  switch (fromUnit) {
    case 'km': meters = value * 1000; break;
    case 'mi': meters = value * 1609.344; break;
    default:   meters = value; break; // 'm' or default = meters
  }
  // Then convert to target unit
  switch (toUnit) {
    case 'km': return meters / 1000;
    case 'mi': return meters / 1609.344;
    default:   return meters; // 'm'
  }
}

function formatDistanceValue(value: number, unit: DistanceDisplayUnit): string {
  switch (unit) {
    case 'm':  return value.toFixed(0);
    case 'km': return value.toFixed(2);
    case 'mi': return value.toFixed(2);
  }
}

// --- Time conversion helpers ---
/** ORS always returns durations in seconds. Convert to display unit. */
function convertTime(seconds: number, toUnit: TimeDisplayUnit): number {
  switch (toUnit) {
    case 'sec': return seconds;
    case 'min': return seconds / 60;
    case 'hr':  return seconds / 3600;
  }
}

function formatTimeValue(value: number, unit: TimeDisplayUnit): string {
  switch (unit) {
    case 'sec': return value.toFixed(0);
    case 'min': return value.toFixed(1);
    case 'hr':  return value.toFixed(2);
  }
}

const timeUnitLabel: Record<TimeDisplayUnit, string> = { sec: 's', min: 'min', hr: 'h' };

export default function MatrixTable({ response, body, elapsedMs }: Props) {
  const distances: (number | null)[][] | undefined = response.distances;
  const durations: (number | null)[][] | undefined = response.durations;
  const grid = distances || durations;
  if (!grid) return null;

  const apiUnit = body.units || 'm'; // unit that was requested (distances are in this unit)

  const [distUnit, setDistUnit] = useState<DistanceDisplayUnit>(apiUnit as DistanceDisplayUnit);
  const [timeUnit, setTimeUnit] = useState<TimeDisplayUnit>('min');

  const sourcesCount = grid.length;
  const destCount = grid[0]?.length || 0;
  const sources: any[] = response.sources || [];
  const destinations: any[] = response.destinations || [];

  const headerLabel = (snap: any, fallback: number): string =>
    snap?.name || `[${fallback}]`;

  return (
    <div className="matrix-result">
      <div className="meta">
        {sourcesCount} × {destCount} matrix · {elapsedMs} ms
      </div>
      <div className="matrix-unit-selectors" style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '4px 0 6px' }}>
        {distances && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85em' }}>
            Distance unit:
            <select value={distUnit} onChange={(e) => setDistUnit(e.target.value as DistanceDisplayUnit)} style={{ fontSize: '0.9em' }}>
              <option value="m">meters</option>
              <option value="km">km</option>
              <option value="mi">miles</option>
            </select>
          </label>
        )}
        {durations && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85em' }}>
            Time unit:
            <select value={timeUnit} onChange={(e) => setTimeUnit(e.target.value as TimeDisplayUnit)} style={{ fontSize: '0.9em' }}>
              <option value="sec">seconds</option>
              <option value="min">minutes</option>
              <option value="hr">hours</option>
            </select>
          </label>
        )}
      </div>
      <table>
        <thead>
          <tr>
            <th></th>
            {Array.from({ length: destCount }, (_, j) => (
              <th key={j} title={`destination ${j}`}>{headerLabel(destinations[j], j)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: sourcesCount }, (_, i) => (
            <tr key={i}>
              <th title={`source ${i}`}>{headerLabel(sources[i], i)}</th>
              {Array.from({ length: destCount }, (_, j) => {
                const sameIdx = (sources[i]?.location != null && destinations[j]?.location != null)
                  ? sources[i].location.toString() === destinations[j].location.toString()
                  : i === j && sourcesCount === destCount;
                const d = distances ? distances[i]?.[j] : null;
                const t = durations ? durations[i]?.[j] : null;
                if (d == null && t == null) return <td key={j}>—</td>;
                return (
                  <td key={j} className={sameIdx ? 'diag' : undefined}>
                    {d != null && (
                      <span className="dist">
                        {formatDistanceValue(convertDistance(d, apiUnit, distUnit), distUnit)} {distUnit}
                      </span>
                    )}
                    {t != null && (
                      <span className="dur">
                        {formatTimeValue(convertTime(t, timeUnit), timeUnit)} {timeUnitLabel[timeUnit]}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

